import React, { useContext, useMemo, useEffect } from 'react';
import { OneLPSsdkParamsContext } from '../../../onelps-sdk-params-context';
import PropTypes from 'prop-types';
import TextInput from '../TextInput';
import CTAButton from '../../buttons/CTAButton';
import RegFormStyles from '../../../css/components/forms/standard/twoStepForm.css';
import { cookieExist, getCookieValue } from '../../../helpers/browser-cookies';
import Terms from '../Terms';
import IppButtons, { IPP_BUTTONS_STYLE } from '../../ipp/IppButtons';
import AlreadyRegisteredLink, { ALREADY_REGISTERED_LINK_POSITION } from '../../common/AlreadyRegisteredLink';
import { withTwoStepValidation } from '../../../helpers/validation/2step-form-validation';
import { withInlineValidation } from '../../../helpers/validation/inline-validation';
import { withCookieOverlayAfterForm, COOKIE_NOTIFICATION_NAME } from '../../../helpers/cookie-overlay';
import { withIppButtons } from '../../../helpers/ipp-buttons';
import { NicknameFieldContext } from '../../../state-management/contexts/regform-fields/nickname-field-context';
import { PasswordFieldContext } from '../../../state-management/contexts/regform-fields/password-field-context';
import { EmailFieldContext } from '../../../state-management/contexts/regform-fields/email-field-context';
import { TermsCheckboxContext } from '../../../state-management/contexts/regform-checkboxes/terms-checkbox-context';
import ErrorMessage, { ERROR_MESSAGE_STYLES } from '../ErrorMessage';
import { useTranslation } from 'react-i18next';
import { showFirstErrorArray } from '../../../helpers/validation/common';
import FormHeadline from '../FormHeadline';

function TwoStepForm({
	currentStep,
	errDelay,
	onNicknameChangeCallback, onPasswordChangeCallback, onEmailChangeCallback, onTermsCheckedCallback,
	handleContinueClick, handlePlayClick, handleIppLinkClick,
	showNicknameLoadingSpinner,
	showPasswordLoadingSpinner,
	showEmailLoadingSpinner,
	showLoadingIndicator,
}) {
	const [nicknameState, nicknameDispatch] = useContext(NicknameFieldContext);
	const passwordState = useContext(PasswordFieldContext)[0];
	const emailState = useContext(EmailFieldContext)[0];
	const [termsState, termsDispatch] = useContext(TermsCheckboxContext);
	const onelpsParams = useContext(OneLPSsdkParamsContext);

	const { t } = useTranslation('translation');

	const buttonLabelKeyStep1 = onelpsParams.form.buttonTranslationKeys[0];
	const buttonLabelKeyStep2 = onelpsParams.form.buttonTranslationKeys[1] || onelpsParams.form.buttonTranslationKeys[0];

	// The order of the states in the argument of showFirstErrorArray
	// is the same as the order of the ErrorMessage components in TwoStepForm.
	const showErrorMessage = useMemo(
		() => showFirstErrorArray([nicknameState, passwordState, emailState, termsState]),
		[emailState, nicknameState, passwordState, termsState]
	);

	useEffect(() => {
		if(!onelpsParams.form.showTermsCheckbox){
			termsDispatch({ type: 'SET_TC_VALUE', payload: true });
		}
	}, [onelpsParams.form.showTermsCheckbox, termsDispatch]);

	return (
		<section className={`${RegFormStyles.formContainer} ${RegFormStyles[onelpsParams.gameId]}`}>
			<form className={RegFormStyles.form}>

				{onelpsParams.form.showHeadline && (
					<header className={RegFormStyles.formHeader}>
						{currentStep === 1 && (
							<FormHeadline text={t('twoStepForm.header.step1')} />
						)}

						{currentStep === 2 && (
							<FormHeadline text={t('twoStepForm.header.step2')} />
						)}
					</header>
				)}

				<main className={RegFormStyles.formMain}>
					<div className={RegFormStyles.fieldWrapper}>
						<TextInput
							type="text"
							id="nickname-input"
							labelText={t('form.username.label')}
							extraMargin
							onChangeCallback={onNicknameChangeCallback}
							hintMessagesList={[t('form.username.hint1'), t('form.username.hint2')]}
							value={nicknameState.value}
							valid={nicknameState.valid}
							showLoadingSpinner={showNicknameLoadingSpinner}
							disabled={showLoadingIndicator}
						/>
						{showErrorMessage[0] && (
							<div className={RegFormStyles.errorWrapper}>
								<ErrorMessage
									text={nicknameState.error.message}
									suggestions={nicknameState.suggestions}
									errorDelay={errDelay}
									style={ERROR_MESSAGE_STYLES.STANDARD}
									onSuggestionClick={(suggestedName) => {
										nicknameDispatch({ type:'SET_NICKNAME_VALUE', payload: suggestedName });
										nicknameDispatch({ type:'SET_NICKNAME_ERROR', payload: null });
										nicknameDispatch({ type:'SET_NICKNAME_VALIDITY', payload: true });
									}}
								/>
							</div>
						)}

					</div>
					<div className={`${RegFormStyles.fieldWrapper} ${currentStep === 1 && RegFormStyles.fieldWrapperNoMargin}`}>
						<TextInput
							type="password"
							id="password-input"
							labelText={t('form.password.label')}
							extraMargin={currentStep === 1 ? false : true}
							onChangeCallback={onPasswordChangeCallback}
							hintMessagesList={[t('form.password.hint1'), t('form.password.hint2')]}
							value={passwordState.value}
							valid={passwordState.valid}
							showLoadingSpinner={showPasswordLoadingSpinner}
							disabled={showLoadingIndicator}
						/>
						{showErrorMessage[1] && (
							<div className={RegFormStyles.errorWrapper}>
								<ErrorMessage
									text={passwordState.error.message}
									errorDelay={errDelay}
									style={ERROR_MESSAGE_STYLES.STANDARD}
								/>
							</div>
						)}
					</div>
					{currentStep === 2 && (
						<div>
							<div className={RegFormStyles.fieldWrapper}>
								<TextInput
									type="email"
									id="email-input"
									labelText={t('form.email.label')}
									onChangeCallback={onEmailChangeCallback}
									hintMessagesList={[t('form.email.hint1')]}
									value={emailState.value}
									valid={emailState.valid}
									showLoadingSpinner={showEmailLoadingSpinner}
									disabled={showLoadingIndicator}
								/>
								{showErrorMessage[2] && (
									<div className={RegFormStyles.errorWrapper}>
										<ErrorMessage
											text={emailState.error.message}
											errorDelay={errDelay}
											style={ERROR_MESSAGE_STYLES.STANDARD}
										/>
									</div>
								)}

							</div>
							<div className={RegFormStyles.fieldWrapper}>
								<Terms
									onChangeCallback={onTermsCheckedCallback}
									showCheckbox={onelpsParams.form.showTermsCheckbox}
								>
								</Terms>
								{showErrorMessage[3] && (
									<div className={RegFormStyles.errorWrapper}>
										<ErrorMessage
											text={termsState.error.message}
											errorDelay={errDelay}
											style={ERROR_MESSAGE_STYLES.STANDARD}
										/>
									</div>
								)}
							</div>
						</div>
					)}
					<div className={RegFormStyles.ctaButtonWrapper}>
						<CTAButton
							onButtonClick={currentStep === 1 ? handleContinueClick : handlePlayClick}
							buttonLabel={currentStep === 1 ? t(buttonLabelKeyStep1) :  t(buttonLabelKeyStep2)}
							isDisabled={showLoadingIndicator}
						/>
					</div>
				</main>

				<footer className={RegFormStyles.formFooter}>
					{onelpsParams.ippLinks.length > 0 && (
						<div className={RegFormStyles.ippWrapper}>
							<IppButtons
								captionText={t('twoStepForm.ipp.caption')}
								style={IPP_BUTTONS_STYLE.MAIN_FORM}
								cookieNotificationValue={cookieExist(COOKIE_NOTIFICATION_NAME) ? getCookieValue(COOKIE_NOTIFICATION_NAME) : ''}
								onLinkClick={(e, link) => handleIppLinkClick(e, link, false)}
								captionPosition={onelpsParams.form.ippCaptionPosition}
							/>
							<div className={RegFormStyles.ippDivider}></div>
						</div>
					)}
					{onelpsParams.form.showAlreadyRegisteredLink && (
						<div className={RegFormStyles.alreadyRegisteredWrapper}>
							<AlreadyRegisteredLink
								linkText={t('loginForm.alreadyRegistered')}
								position={ALREADY_REGISTERED_LINK_POSITION.FORM}
							/>
						</div>
					)}
				</footer>
			</form>
		</section>
	);
}

TwoStepForm.propTypes = {
	currentStep: PropTypes.number.isRequired,
	errDelay: PropTypes.number.isRequired,
	onNicknameChangeCallback: PropTypes.func.isRequired,
	onPasswordChangeCallback: PropTypes.func.isRequired,
	onEmailChangeCallback: PropTypes.func.isRequired,
	handleContinueClick: PropTypes.func.isRequired,
	handlePlayClick: PropTypes.func.isRequired,
	handleIppLinkClick: PropTypes.func.isRequired,
	onTermsCheckedCallback: PropTypes.func.isRequired,
	showNicknameLoadingSpinner: PropTypes.bool.isRequired,
	showPasswordLoadingSpinner: PropTypes.bool.isRequired,
	showEmailLoadingSpinner: PropTypes.bool.isRequired,
	showLoadingIndicator: PropTypes.bool.isRequired,
};

const EnhancedTwoStepForm =
withIppButtons(
	withCookieOverlayAfterForm(
		withTwoStepValidation(
			withInlineValidation(
				TwoStepForm
			)
		)
	)
);
export default EnhancedTwoStepForm;
